// LevelScene: el mundo jugable. Cada reto es una "Zona de Decisión Matemática":
// la cámara se bloquea, el jugador queda sobre una plataforma segura y las TRES
// respuestas se ven a la vez dentro del viewport, con saltos fáciles y tolerantes.
// La lógica de datos (puntuación, estado) vive fuera de esta escena (GameState/ScoreSystem).

import Phaser from 'phaser';
import { GAME, HEX, CSS, FONTS, PHYS, LEVEL, getAnswerGemTheme, getGameplayUiTheme } from '../config';
import { Player } from '../entities/Player';
import { GameState } from '../systems/GameState';
import { mathEngine } from '../math/MathEngine';
import { SUMA_MISSIONS, CHALLENGES_PER_MISSION } from '../planets/additionMissions';
import { RESTA_MISSIONS } from '../planets/subtractionMissions';
import { MULTIPLICACION_MISSIONS } from '../planets/multiplicationMissions';
import { DIVISION_MISSIONS } from '../planets/divisionMissions';
import { getBands } from '../util/responsive';
import { ProgressData as PlayerData } from '../systems/ProgressData';
import { getNextCosmicValueMessage } from '../data/cosmicValueMessages';
import { startScenePlay } from '../systems/PlayAuthorization';

const ZONE_W = 1160; // ancho de la zona de decisión (cabe dentro del viewport de 1280)
const ANS_Y = 380; // altura de las plataformas de respuesta
const ANS_W = 200; // ancho de cada plataforma de respuesta
const ANSWER_PLAT_H = 48; // altura exclusiva: conserva el borde superior y libera espacio debajo
const answerFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

export class LevelScene extends Phaser.Scene {
  constructor() {
    super('LevelScene');
  }

  create() {
    return startScenePlay(this, 'adventure', ticket => this.createRun(ticket));
  }

  createRun(ticket) {
    this.progressAttemptId = PlayerData.beginAttempt?.(ticket);
    this.playAuthorizationActive = !!ticket;
    this.audio = this.registry.get('audio');
    this.physics.world.gravity.y = PHYS.GRAVITY;
    this.planetId = this.registry.get('currentPlanet') || 'suma';
    this.isResta = this.planetId === 'resta';
    this.isMult = this.planetId === 'mult';
    this.isDivision = this.planetId === 'division';
    // La gema coleccionable mantiene una misma base; solo su halo hereda el mundo.
    this.collectibleTheme = getGameplayUiTheme(this.planetId);

    // Cada planeta comparte la jugabilidad, pero conserva sus propias misiones y ejercicios.
    const missions = this.isResta ? RESTA_MISSIONS : this.isMult ? MULTIPLICACION_MISSIONS : this.isDivision ? DIVISION_MISSIONS : SUMA_MISSIONS;
    this.mission = this.registry.get('currentMissionData') || missions[0];
    this.devPreview = this.registry.get('devPreviewConfig') || null;
    this.registry.set('devPreviewConfig', null);
    const count = CHALLENGES_PER_MISSION;
    // La práctica inteligente previa permanece aislada del recorrido oficial.
    this.practiceMode = !!this.registry.get('adaptivePractice');
    this.registry.set('adaptivePractice', false);
    this.practiceFocus = this.practiceMode
      ? PlayerData.getPracticeFocus(this.planetId, this.mission.index)
      : [];

    this.gs = new GameState({ time: this.mission.time, challenges: count });
    this.registry.set('gameState', this.gs);

    // Ejercicios generados dinámicamente: sin duplicados, con variedad de
    // números y, en práctica inteligente, hasta tres retos de refuerzo.
    this.problems = mathEngine.generateMissionSet(
      this.mission.type,
      count,
      this.isResta ? 'resta' : this.isMult ? 'mult' : this.isDivision ? 'division' : 'suma',
      { focusTags: this.practiceFocus },
    );
    this.zones = [];
    this.activeIndex = -1;
    this.activeZone = null;
    this.answered = false;
    this.levelEnded = false;
    this.missionPaused = false;
    this.cameraLocked = false;
    this.respawnQueued = false;
    this.tutorialAnswerHintPending = false;
    this.cosmicCapsuleSessionId = null;
    this.handleCosmicCapsuleComplete = event => {
      const sessionId = event.detail?.sessionId;
      if (!sessionId || sessionId !== this.cosmicCapsuleSessionId) return;
      this.cosmicCapsuleSessionId = null;
      this.showMissionResults();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('tmag:cosmic-value-capsule-complete', this.handleCosmicCapsuleComplete);
      this.events.once('shutdown', () =>
        window.removeEventListener('tmag:cosmic-value-capsule-complete', this.handleCosmicCapsuleComplete),
      );
    }
    this.isTouchDevice =
      (this.sys.game.device.input && this.sys.game.device.input.touch) ||
      (typeof window !== 'undefined' &&
        (window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0));
    // La guía ocurre una sola vez por navegador/perfil y no modifica la partida.
    this.tutorialActive = !this.devPreview && !PlayerData.hasSeenTutorial();
    this.tutorialStep = null;
    if (this.tutorialActive) this.gs.setClockActive(false);
    this.checkpoint = { x: 140, y: LEVEL.PLAT_TOP - 60 };

    this.buildBackground();
    this.platforms = this.physics.add.staticGroup();
    this.answerGroup = this.physics.add.staticGroup();
    this.bonusCrystals = [];
    // Cada planeta puede usar su propia baraja de recorridos sin repetir
    // el mismo tramo inmediatamente.
    this.sumTransitionOrder = !this.isResta && !this.isMult && !this.isDivision
      ? this.createTransitionOrder(Math.max(0, this.problems.length - 1))
      : [];
    this.subtractionTransitionOrder = this.isResta
      ? this.createTransitionOrder(Math.max(0, this.problems.length - 1))
      : [];
    this.multiplicationTransitionOrder = this.isMult
      ? this.createTransitionOrder(Math.max(0, this.problems.length - 1))
      : [];
    this.divisionTransitionOrder = this.isDivision
      ? this.createTransitionOrder(Math.max(0, this.problems.length - 1))
      : [];

    this.buildLevel();
    this.buildPlayer();
    this.buildInput();
    this.applyLayout();
    this.scale.on('resize', this.applyLayout, this);
    this.events.once('shutdown', () => this.scale.off('resize', this.applyLayout, this));

    this.scene.launch('HUDScene');
    // HUD ya está listo antes de enviar el primer paso de la guía.
    if (this.tutorialActive) this.time.delayedCall(450, () => this.startTutorial());

    this.gs.once('gameover', ({ won }) => this.endLevel(won));
    this.gs.on('correct', ({ streak }) => this.audio && this.audio.playCorrect(streak));
    this.gs.on('wrong', () => this.audio && this.audio.playWrong());

    this.cameras.main.fadeIn(300, 6, 3, 20);
    if (this.devPreview) this.time.delayedCall(120, () => this.runDevFinalPreview());
  }

  buildBackground() {
    const { WIDTH, HEIGHT } = GAME;
    const skyKey = this.isResta ? 'bg_resta_day' : this.isMult ? 'bg_multiplicacion_forest' : this.isDivision ? 'bg_division_ocean' : 'bg_deepspace';
    // Multiplicación usa un solo panorama: nunca se repite como mosaico.
    if (this.isMult || this.isDivision) {
      this.skyFar = this.add.image(WIDTH / 2, HEIGHT / 2, skyKey).setOrigin(0.5).setScrollFactor(0).setDepth(-5);
    } else {
      this.skyFar = this.add.tileSprite(0, 0, WIDTH, HEIGHT, skyKey).setOrigin(0).setScrollFactor(0);
      this.skyFar.setTileScale(WIDTH / (this.isResta ? 1280 : 1264), HEIGHT / (this.isResta ? 720 : 848));
    }
    this.planetMid = this.add
      .tileSprite(0, HEIGHT, WIDTH, 460, 'bg_planet')
      .setOrigin(0, 1)
      .setScrollFactor(0)
      .setAlpha(0.92);
    this.planetMid.setTileScale(WIDTH / 1264, 460 / 848);
    if (this.isResta || this.isMult || this.isDivision) {
      // El paisaje diurno ya contiene montañas, cascadas, sol y lunas.
      this.planetMid.setVisible(false);
    }

    this.starLayer = this.add.container(0, 0);
    for (let i = 0; i < 80; i++) {
      const s = this.add
        .image(Phaser.Math.Between(0, WIDTH), Phaser.Math.Between(0, HEIGHT - 200), 'star')
        .setScale(Phaser.Math.FloatBetween(0.3, 1))
        .setScrollFactor(0.4)
        .setAlpha((this.isResta || this.isMult || this.isDivision) ? 0 : Phaser.Math.FloatBetween(0.2, 0.8));
      this.starLayer.add(s);
    }

    this.dust = this.add.particles(0, 0, 'spark', {
      x: { min: 0, max: WIDTH },
      y: { min: 0, max: HEIGHT },
      lifespan: 4000,
      speedY: this.isResta ? { min: -10, max: 8 } : this.isMult ? { min: -14, max: 6 } : this.isDivision ? { min: -18, max: -4 } : { min: -8, max: 8 },
      speedX: this.isResta ? { min: 4, max: 18 } : this.isMult ? { min: -5, max: 12 } : this.isDivision ? { min: -8, max: 8 } : { min: -6, max: 6 },
      scale: { start: 0.4, end: 0 },
      alpha: { start: 0.4, end: 0 },
      frequency: 220,
      tint: this.isResta ? [0xffffff, 0xffe89a, 0x8ff5e5] : this.isMult ? [HEX.cyan, HEX.violet, HEX.gold] : this.isDivision ? [0x75f0ff, 0xb7a3ff, 0xffffff] : [HEX.cyan, HEX.violet, HEX.magenta],
      blendMode: 'ADD',
    });
    this.dust.setScrollFactor(0.3).setDepth(1);
  }

  addPlatform(x, top, w, tint) {
    const platformKey = this.isResta ? 'platform_resta' : this.isDivision ? 'platform_division' : 'platform';
    const p = this.platforms.create(x + w / 2, top + LEVEL.PLAT_H / 2, platformKey);
    p.setDisplaySize(w, LEVEL.PLAT_H).refreshBody();
    p.setDepth(5);
    if (tint) p.setTint(tint);
    // Planeta Resta usa su textura clara propia, sin filtros de color.
    return p;
  }

  getOperationLesson() {
    if (this.isResta) return { text: 'MINUENDO − SUSTRAENDO = DIFERENCIA', color: '#bfffe9' };
    if (this.isMult) return { text: 'FACTOR × FACTOR = PRODUCTO', color: '#f9e78d' };
    if (this.isDivision) return { text: 'DIVIDENDO ÷ DIVISOR = COCIENTE', color: '#b9f5ff' };
    return { text: 'SUMANDO + SUMANDO = TOTAL', color: '#b7fff7' };
  }

  createTransitionOrder(total) {
    const types = [0, 1, 2, 3, 4, 5];
    if (total <= 0) return [];

    // Primera vuelta: los seis estilos aparecen una vez en orden aleatorio.
    const order = Phaser.Utils.Array.Shuffle([...types]);

    // En una misión de 10 retos sobran tres tramos. Se toman de una segunda
    // baraja sin repetición inmediata, para que cada partida sea distinta.
    const extras = Phaser.Utils.Array.Shuffle([...types]);
    while (extras.length && extras[0] === order[order.length - 1]) {
      extras.push(extras.shift());
    }
    order.push(...extras.slice(0, Math.max(0, total - types.length)));

    // Mantiene el contrato incluso si luego se cambia la cantidad de retos.
    return order.slice(0, total);
  }

  buildLevel() {
    let cx = 40;

    // Plataforma de inicio.
    this.addPlatform(cx, LEVEL.PLAT_TOP, 360);
    // Franja educativa común: relaciona de forma visible cada mundo con
    // el vocabulario matemático que el estudiante está trabajando.
    const operationLesson = this.getOperationLesson();
    const operationLessonY = ANS_Y;
    this.add
      .text(cx + 180, operationLessonY, operationLesson.text, {
        fontFamily: FONTS.friendly,
        fontSize: '18px',
        fontStyle: '800',
        color: '#ffffff',
        backgroundColor: '#071735',
        padding: { x: 18, y: 9 },
      })
      .setOrigin(0.5)
      .setDepth(12)
      .setStroke('#020814', 3)
      .setShadow(0, 3, '#000000', 4, true, true);
    cx += 360;

    for (let i = 0; i < this.problems.length; i++) {
      cx = this.buildDecisionZone(cx, i, this.problems[i]);
      // Nueve transiciones: en Suma los tres recorridos se repiten 3 veces cada uno.
      if (i < this.problems.length - 1) cx = this.buildInterRun(cx, i);
    }

    // Meta final: portal.
    this.addPlatform(cx, LEVEL.PLAT_TOP, 380);
    const goalKey = this.isResta ? 'portal_resta' : this.isMult ? 'portal_multiplicacion' : this.isDivision ? 'portal_division' : 'portal';
    const goal = this.physics.add.staticImage(cx + 210, LEVEL.PLAT_TOP - 90, goalKey).setScale(1.1).setDepth(6);
    this.tweens.add({ targets: goal, angle: 360, duration: 6000, repeat: -1 });
    this.tweens.add({ targets: goal, scale: 1.2, duration: 1200, yoyo: true, repeat: -1, ease: 'Sine.inOut' });
    this.add
      .text(cx + 210, LEVEL.PLAT_TOP - 190, 'META', {
        fontFamily: FONTS.display,
        fontSize: '28px',
        fontStyle: '900',
        color: CSS.gold,
        backgroundColor: '#061329',
        padding: { x: 18, y: 8 },
      })
      .setOrigin(0.5)
      .setDepth(12)
      .setStroke('#020814', 4)
      .setShadow(0, 3, '#000000', 5, true, true);
    this.goal = goal;

    this.levelWidth = cx + 560;
    this.goalX = cx + 210;
    this.physics.world.setBounds(0, -600, this.levelWidth, GAME.HEIGHT + 1000);
    this.cameras.main.setBounds(0, 0, this.levelWidth, GAME.HEIGHT);
  }

  getOperationMotif() {
    if (this.isResta) return { symbol: '−', color: 0x8ff5e5, accent: 0xffe89a };
    if (this.isMult) return { symbol: '×', color: HEX.gold, accent: 0xfff0a4 };
    if (this.isDivision) return { symbol: '÷', color: 0x72fff6, accent: 0x8e86d8 };
    return { symbol: '+', color: HEX.cyan, accent: HEX.magenta };
  }

  // Detalles ambientales: la operación se siente presente en cada zona, pero
  // nunca ocupa el espacio de las tres respuestas ni modifica la mecánica.
  addOperationMotifs(x, top) {
    const motif = this.getOperationMotif();
    const marks = [
      { dx: 20, dy: -205, size: 23 },
      { dx: 74, dy: -234, size: 17 },
      { dx: 124, dy: -195, size: 20 },
    ];
    marks.forEach(({ dx, dy, size }, index) => {
      const mark = this.add
        .text(x + dx, top + dy, motif.symbol, {
          fontFamily: FONTS.friendly,
          fontSize: `${size}px`,
          fontStyle: '900',
          color: Phaser.Display.Color.IntegerToColor(index % 2 ? motif.accent : motif.color).rgba,
        })
        .setOrigin(0.5)
        .setDepth(3)
        .setAlpha(0.62);
      this.tweens.add({
        targets: mark,
        y: mark.y - 8,
        alpha: 0.32,
        duration: 1150 + index * 150,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.inOut',
      });
    });

    // En el arrecife los símbolos viajan acompañados por burbujas suaves,
    // reforzando la idea de repartir sin cambiar ninguna respuesta.
    if (this.isDivision) {
      [44, 102, 154].forEach((dx, index) => {
        const bubble = this.add
          .circle(x + dx, top - 170 - index * 16, 6 + index * 2, 0x75f0ff, 0.12)
          .setStrokeStyle(1.5, 0xc9fbff, 0.7)
          .setDepth(3);
        this.tweens.add({
          targets: bubble,
          y: bubble.y - 14,
          alpha: 0.25,
          duration: 1300 + index * 170,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.inOut',
        });
      });
    }
  }

  buildDecisionZone(zx, index, problem) {
    const top = LEVEL.PLAT_TOP;
    const centerX = zx + ZONE_W / 2;
    // Portal y gemas responden a la misma identidad cromática del mundo.
    const answerGemTheme = getAnswerGemTheme(
      this.isResta ? 'resta' : this.isMult ? 'multiplicacion' : this.isDivision ? 'division' : 'suma',
    );

    // Plataforma segura amplia: el jugador no puede caer accidentalmente durante el reto.
    this.addPlatform(zx, top, ZONE_W);

    // Portal decorativo de entrada.
    const portalKey = this.isResta ? 'portal_resta' : this.isMult ? 'portal_multiplicacion' : this.isDivision ? 'portal_division' : 'portal';
    const portal = this.add.image(zx + 90, top - 96, portalKey).setScale(0.5).setDepth(4).setAlpha(0.8);
    // Multiplicación ya tiene una textura dorada propia: no se mezcla con el aro multicolor base.
    if (!this.isMult) portal.setTint(answerGemTheme.tint);
    this.tweens.add({ targets: portal, angle: 360, duration: 5000, repeat: -1 });
    this.addOperationMotifs(zx, top);

    // Tres rutas de respuesta, siempre visibles y justas. Suma alterna
    // tres composiciones suaves, sin esconder ni hacer más difícil una opción.
    const sumRouteLayouts = [
      { spread: 360, answerY: ANS_Y }, // abanico clásico
      { spread: 320, answerY: ANS_Y }, // puentes de energía
      { spread: 390, answerY: ANS_Y }, // islas más abiertas
    ];
    const sumRoute = !this.isResta && !this.isMult && !this.isDivision
      ? sumRouteLayouts[index % sumRouteLayouts.length]
      : { spread: 360, answerY: ANS_Y };
    const answerY = sumRoute.answerY;
    const xs = [centerX - sumRoute.spread, centerX, centerX + sumRoute.spread];
    // Las tres opciones son equivalentes antes de responder: comparten una sola
    // identidad visual por mundo y nunca revelan pistas por color.
    const platformsData = [];
    xs.forEach((axc, i) => {
      const choice = problem.choices[i];
      const platformKey = this.isResta ? 'platform_resta' : this.isDivision ? 'platform_division' : 'platform';
      const plat = this.answerGroup.create(axc, answerY + ANSWER_PLAT_H / 2, platformKey);
      plat.setDisplaySize(ANS_W, ANSWER_PLAT_H).refreshBody();
      plat.setDepth(5);
      // La textura de Resta ya tiene piedra clara y vegetación.
      // Plataforma "atravesable": solo colisiona por arriba -> saltos muy tolerantes,
      // el jugador puede pasar desde abajo y aterrizar encima sin golpearse.
      plat.body.checkCollision.down = false;
      plat.body.checkCollision.left = false;
      plat.body.checkCollision.right = false;
      plat.setData('challenge', index);
      plat.setData('index', i);
      plat.setData('value', choice.value);
      plat.setData('correct', choice.correct);

      const advancedMission = this.mission.index >= 3;
      const labelY = answerY - 60;
      // Incluso en misiones avanzadas, gema y respuesta comparten el centro.
      // El texto queda encima por profundidad; la gema conserva su función de fondo.
      const crystalY = labelY;
      const gemOutline = answerGemTheme.outlineTint
        ? this.add.image(axc, crystalY, 'crystal')
          .setScale(answerGemTheme.scale * answerGemTheme.outlineScale)
          .setDepth(5)
          .setTint(answerGemTheme.outlineTint)
          .setAlpha(answerGemTheme.outlineAlpha)
        : null;
      const crystal = this.add.image(axc, crystalY, 'crystal')
        .setScale(answerGemTheme.scale)
        .setDepth(6)
        .setTint(answerGemTheme.tint);
      this.tweens.add({
        targets: gemOutline ? [gemOutline, crystal] : crystal,
        y: crystal.y - 12,
        duration: 1400,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.inOut',
      });
      const labelText = choice.label || answerFormat.format(choice.value);
      const isDetailedAnswer = labelText.includes('\n');
      const baseFontSize = isDetailedAnswer ? 30 : advancedMission ? 46 : 44;
      const label = this.add
        .text(axc, labelY, labelText, {
          // Los títulos pueden ser futuristas; los resultados deben ser
          // especialmente claros para que 0, 1 y las comas se distingan bien.
          fontFamily: FONTS.friendly,
          fontSize: `${baseFontSize}px`,
          fontStyle: '900',
          color: '#ffffff',
          align: 'center',
          lineSpacing: -4,
        })
        .setOrigin(0.5)
        .setDepth(16)
        .setStroke('#061329', isDetailedAnswer ? 7 : 8)
        .setShadow(0, 3, '#000000', 7, true, true)
        .setData('advancedMission', advancedMission)
        .setData('baseFontSize', baseFontSize)
        .setData('maxAnswerWidth', isDetailedAnswer ? 250 : 270);

      platformsData.push({
        plat,
        crystal,
        label,
        home: { x: plat.x, y: plat.y },
        crystalHome: crystal.y,
        labelHome: label.y,
      });
    });

    this.zones.push({
      index,
      problem,
      centerX,
      spawnX: zx + 120,
      minX: zx + 46,
      maxX: zx + ZONE_W - 60,
      activationX: zx + 120,
      platforms: platformsData,
      correctIndex: problem.correctIndex,
      portal,
      routeStyle: this.isResta || this.isMult || this.isDivision ? 'classic' : index % 3,
      solved: false,
    });

    return zx + ZONE_W;
  }

  addSegmentedSafePath(cx, top, widths) {
    let x = cx;
    widths.forEach((width) => {
      this.addPlatform(x, top, width);
      x += width;
    });
  }

  addBonusCrystal(x, y) {
    // La misma silueta distingue a todo objeto coleccionable del recorrido.
    // El núcleo claro es universal; el contorno y halo comunican el mundo actual.
    const accent = this.collectibleTheme?.tint || HEX.cyan;
    const halo = this.add.circle(x, y, 42, accent, 0.16)
      .setDepth(5)
      .setBlendMode(Phaser.BlendModes.ADD);
    const glowGem = this.add.image(x, y, 'crystal')
      .setScale(1.14)
      .setDepth(6)
      .setTint(accent)
      .setAlpha(0.62);
    const c = this.add.image(x, y, 'crystal')
      .setScale(1)
      .setDepth(7)
      .setTint(HEX.white);

    // Se conserva exactamente la flotación suave original para todos los mundos.
    this.tweens.add({
      targets: [halo, glowGem, c],
      y: y - 14,
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    });
    c.once('destroy', () => {
      halo.destroy();
      glowGem.destroy();
    });
    this.physics.add.existing(c, true);
    c.body.setSize(46, 62);
    this.bonusCrystals.push(c);
    return c;
  }

  buildSumInterRun(cx, transitionIndex) {
    const top = LEVEL.PLAT_TOP;
    // Orden aleatorio controlado: las seis formas aparecen antes de repetir.
    const pattern = this.sumTransitionOrder[transitionIndex] ?? (transitionIndex % 6);

    if (pattern === 0) {
      // 1. Puente de energía: avance fluido y cristal al final.
      this.addPlatform(cx, top, 500);
      [110, 230, 350].forEach((dx, i) => {
        const spark = this.add.circle(cx + dx, top - 38 - (i % 2) * 12, 7, HEX.cyan, 0.62).setDepth(6);
        this.tweens.add({ targets: spark, alpha: 0.2, y: spark.y - 12, duration: 900 + i * 120, yoyo: true, repeat: -1 });
      });
      this.addBonusCrystal(cx + 420, top - 118, HEX.green);
    } else if (pattern === 1) {
      // 2. Pasarela de energía: tres secciones contiguas, sin capas superpuestas.
      this.addSegmentedSafePath(cx, top, [154, 192, 154]);
      [110, 250, 390].forEach((dx, i) => this.add.circle(cx + dx, top - 42 - (i % 2) * 14, 7, HEX.gold, 0.5).setDepth(6));
      this.addBonusCrystal(cx + 268, top - 142, HEX.gold);
    } else if (pattern === 2) {
      // 3. Corredor portal: cuatro paneles unidos hacia el portal.
      this.addSegmentedSafePath(cx, top, [96, 144, 128, 132]);
      const gate = this.add.image(cx + 250, top - 108, 'portal').setScale(0.42).setDepth(5).setTint(HEX.violet).setAlpha(0.72);
      this.tweens.add({ targets: gate, angle: 360, duration: 4200, repeat: -1 });
      this.addBonusCrystal(cx + 422, top - 126, HEX.magenta);
    } else if (pattern === 3) {
      // 4. Salto lunar: dos huecos reales; si cae, reaparece en el checkpoint.
      this.addPlatform(cx + 18, top - 18, 190);
      this.addPlatform(cx + 280, top - 38, 220);
      this.addBonusCrystal(cx + 315, top - 164, HEX.cyan);
    } else if (pattern === 4) {
      // 5. Escalones flotantes: recorrido de precisión moderada, sin plataformas ocultas.
      this.addPlatform(cx + 12, top - 16, 100);
      this.addPlatform(cx + 150, top - 68, 104);
      this.addPlatform(cx + 298, top - 106, 104);
      this.addPlatform(cx + 440, top - 48, 60);
      this.addBonusCrystal(cx + 406, top - 140, HEX.gold);
    } else {
      // 6. Islas de cometa: tres islotes separados, con un bono elevado al final.
      this.addPlatform(cx + 12, top - 26, 150);
      this.addPlatform(cx + 224, top - 92, 152);
      this.addPlatform(cx + 428, top - 22, 72);
      this.addBonusCrystal(cx + 456, top - 145, HEX.magenta);
    }
    return cx + 500;
  }

  buildSubtractionInterRun(cx, transitionIndex) {
    const top = LEVEL.PLAT_TOP;
    const pattern = this.subtractionTransitionOrder[transitionIndex] ?? (transitionIndex % 6);
    const aqua = 0x8ff5e5;
    const sun = 0xffe89a;

    if (pattern === 0) {
      // 1. Sendero de diferencia: camino claro con fragmentos que se alejan.
      this.addPlatform(cx, top, 500);
      [118, 250, 380].forEach((dx, i) => {
        const fragment = this.add.circle(cx + dx, top - 38 - (i % 2) * 13, 7, aqua, 0.6).setDepth(6);
        this.tweens.add({ targets: fragment, x: fragment.x - 12, alpha: 0.16, duration: 1000 + i * 130, yoyo: true, repeat: -1 });
      });
      this.addBonusCrystal(cx + 420, top - 118, aqua, 'crystal_resta');
    } else if (pattern === 1) {
      // 2. Sendero de fragmentos: tres tramos unidos, sin capas apiladas.
      this.addSegmentedSafePath(cx, top, [154, 192, 154]);
      [108, 252, 394].forEach((dx, i) => this.add.circle(cx + dx, top - 42 - (i % 2) * 14, 7, i % 2 ? sun : aqua, 0.5).setDepth(6));
      this.addBonusCrystal(cx + 266, top - 142, sun, 'crystal_resta');
    } else if (pattern === 2) {
      // 3. Umbral de resta: cuatro tramos unidos hacia el portal.
      this.addSegmentedSafePath(cx, top, [96, 144, 128, 132]);
      const gate = this.add.image(cx + 250, top - 108, 'portal_resta').setScale(0.42).setDepth(5).setAlpha(0.74);
      this.tweens.add({ targets: gate, angle: 360, duration: 4200, repeat: -1 });
      this.addBonusCrystal(cx + 422, top - 126, aqua, 'crystal_resta');
    } else if (pattern === 3) {
      // 4. Grietas de cristal: dos huecos reales y regreso al checkpoint al caer.
      this.addPlatform(cx + 18, top - 18, 190);
      this.addPlatform(cx + 280, top - 38, 220);
      this.addBonusCrystal(cx + 315, top - 164, sun, 'crystal_resta');
    } else if (pattern === 4) {
      // 5. Fragmentos flotantes: tres plataformas separadas, sin red inferior.
      this.addPlatform(cx + 12, top - 16, 100);
      this.addPlatform(cx + 150, top - 68, 104);
      this.addPlatform(cx + 298, top - 106, 104);
      this.addPlatform(cx + 440, top - 48, 60);
      this.addBonusCrystal(cx + 406, top - 140, aqua, 'crystal_resta');
    } else {
      // 6. Islas que quedan: terreno reducido con huecos suaves entre islotes.
      this.addPlatform(cx + 12, top - 26, 150);
      this.addPlatform(cx + 224, top - 92, 152);
      this.addPlatform(cx + 428, top - 22, 72);
      this.addBonusCrystal(cx + 456, top - 145, sun, 'crystal_resta');
    }
    return cx + 500;
  }

  buildMultiplicationInterRun(cx, transitionIndex) {
    const top = LEVEL.PLAT_TOP;
    const pattern = this.multiplicationTransitionOrder[transitionIndex] ?? (transitionIndex % 6);
    const leaf = HEX.green;
    const pollen = HEX.gold;
    const bloom = HEX.violet;

    if (pattern === 0) {
      // 1. Raíces enlazadas: sendero continuo con energía que se repite en grupos.
      this.addPlatform(cx, top, 500);
      [92, 118, 236, 262, 380, 406].forEach((dx, i) => {
        const seed = this.add.circle(cx + dx, top - 42 - (i % 2) * 8, 5, i % 3 ? leaf : pollen, 0.65).setDepth(6);
        this.tweens.add({ targets: seed, y: seed.y - 10, alpha: 0.2, duration: 900 + i * 80, yoyo: true, repeat: -1 });
      });
      this.addBonusCrystal(cx + 420, top - 118, leaf);
    } else if (pattern === 1) {
      // 2. Hongos en grupos: tres raíces unidas, sin plataformas apiladas.
      this.addSegmentedSafePath(cx, top, [154, 192, 154]);
      [88, 246, 416].forEach((dx) => this.add.circle(cx + dx, top - 62, 14, bloom, 0.35).setDepth(4));
      this.addBonusCrystal(cx + 268, top - 142, pollen);
    } else if (pattern === 2) {
      // 3. Arco de enredaderas: cuatro secciones unidas hacia el portal.
      this.addSegmentedSafePath(cx, top, [96, 144, 128, 132]);
      const gate = this.add.image(cx + 250, top - 108, 'portal_multiplicacion').setScale(0.42).setDepth(5)
        .setAlpha(0.76);
      this.tweens.add({ targets: gate, angle: 360, duration: 4200, repeat: -1 });
      this.addBonusCrystal(cx + 422, top - 126, bloom);
    } else if (pattern === 3) {
      // 4. Raíces cortadas: dos huecos reales; caer devuelve al checkpoint.
      this.addPlatform(cx + 18, top - 18, 190);
      this.addPlatform(cx + 280, top - 38, 220);
      this.addBonusCrystal(cx + 315, top - 164, leaf);
    } else if (pattern === 4) {
      // 5. Flores flotantes: plataformas separadas sin red inferior.
      this.addPlatform(cx + 12, top - 16, 100);
      this.addPlatform(cx + 150, top - 68, 104);
      this.addPlatform(cx + 298, top - 106, 104);
      this.addPlatform(cx + 440, top - 48, 60);
      [70, 230, 398].forEach((dx, i) => this.add.circle(cx + dx, top - 116 - (i % 2) * 18, 10, i % 2 ? bloom : pollen, 0.54).setDepth(5));
      this.addBonusCrystal(cx + 406, top - 140, pollen);
    } else {
      // 6. Islas de semillas: tres claros del bosque y huecos moderados.
      this.addPlatform(cx + 12, top - 26, 150);
      this.addPlatform(cx + 224, top - 92, 152);
      this.addPlatform(cx + 428, top - 22, 72);
      this.addBonusCrystal(cx + 456, top - 145, bloom);
    }
    return cx + 500;
  }

  buildDivisionInterRun(cx, transitionIndex) {
    const top = LEVEL.PLAT_TOP;
    const pattern = this.divisionTransitionOrder[transitionIndex] ?? (transitionIndex % 6);
    const water = 0x75f0ff;
    const pearl = 0xb7a3ff;
    const coral = 0xffa8df;

    if (pattern === 0) {
      // 1. Corriente de perlas: sendero continuo con perlas repartidas en grupos.
      this.addPlatform(cx, top, 500);
      [96, 122, 244, 270, 392, 418].forEach((dx, i) => {
        const bubble = this.add.circle(cx + dx, top - 42 - (i % 2) * 9, 6, i % 3 ? water : pearl, 0.62).setDepth(6);
        this.tweens.add({ targets: bubble, y: bubble.y - 13, alpha: 0.18, duration: 920 + i * 85, yoyo: true, repeat: -1 });
      });
      this.addBonusCrystal(cx + 420, top - 118, water, 'crystal_division');
    } else if (pattern === 1) {
      // 2. Terrazas de coral: tres arrecifes unidos, sin plataformas apiladas.
      this.addSegmentedSafePath(cx, top, [154, 192, 154]);
      [88, 246, 416].forEach((dx, i) => this.add.circle(cx + dx, top - 62, 12, i % 2 ? coral : pearl, 0.38).setDepth(4));
      this.addBonusCrystal(cx + 268, top - 142, pearl, 'crystal_division');
    } else if (pattern === 2) {
      // 3. Portal del cociente: cuatro arrecifes unidos hacia el portal.
      this.addSegmentedSafePath(cx, top, [96, 144, 128, 132]);
      const gate = this.add.image(cx + 250, top - 108, 'portal_division').setScale(0.42).setDepth(5).setAlpha(0.76);
      this.tweens.add({ targets: gate, angle: 360, duration: 4200, repeat: -1 });
      this.addBonusCrystal(cx + 422, top - 126, coral, 'crystal_division');
    } else if (pattern === 3) {
      // 4. Arrecife partido: dos huecos reales; caer devuelve al checkpoint.
      this.addPlatform(cx + 18, top - 18, 190);
      this.addPlatform(cx + 280, top - 38, 220);
      this.addBonusCrystal(cx + 315, top - 164, water, 'crystal_division');
    } else if (pattern === 4) {
      // 5. Burbujas flotantes: plataformas separadas sin red inferior.
      this.addPlatform(cx + 12, top - 16, 100);
      this.addPlatform(cx + 150, top - 68, 104);
      this.addPlatform(cx + 298, top - 106, 104);
      this.addPlatform(cx + 440, top - 48, 60);
      [70, 230, 398].forEach((dx, i) => this.add.circle(cx + dx, top - 118 - (i % 2) * 20, 9, i % 2 ? pearl : water, 0.54).setDepth(5));
      this.addBonusCrystal(cx + 406, top - 140, pearl, 'crystal_division');
    } else {
      // 6. Islas de conchas: tres bancos separados con huecos moderados.
      this.addPlatform(cx + 12, top - 26, 150);
      this.addPlatform(cx + 224, top - 92, 152);
      this.addPlatform(cx + 428, top - 22, 72);
      this.addBonusCrystal(cx + 456, top - 145, coral, 'crystal_division');
    }
    return cx + 500;
  }

  buildInterRun(cx, transitionIndex = 0) {
    if (this.isResta) return this.buildSubtractionInterRun(cx, transitionIndex);
    if (this.isMult) return this.buildMultiplicationInterRun(cx, transitionIndex);
    if (this.isDivision) return this.buildDivisionInterRun(cx, transitionIndex);
    return this.buildSumInterRun(cx, transitionIndex);
  }

  buildPlayer() {
    this.player = new Player(this, this.checkpoint.x, this.checkpoint.y);
    this.player.onJump = () => {
      this.audio && this.audio.playJump();
      if (this.tutorialActive && this.tutorialStep === 'jump') this.advanceTutorial('answer');
    };

    this.physics.add.collider(this.player, this.platforms);
    this.physics.add.collider(this.player, this.answerGroup, this.onAnswerLand, null, this);
    this.bonusCrystals.forEach((c) => this.physics.add.overlap(this.player, c, () => this.collectBonus(c), null, this));
    this.physics.add.overlap(this.player, this.goal, () => this.reachGoal(), null, this);
  }

  // Layout de cámara adaptativo: horizontal ocupa todo; vertical usa banda central 16:9
  // (misma jugabilidad y respuestas visibles); HUD/controles van en HUDScene.
  applyLayout() {
    const W = this.scale.width;
    const H = this.scale.height;
    const b = getBands(W, H);
    const cam = this.cameras.main;
    cam.setViewport(0, b.playY, W, b.playH);
    cam.setZoom(Math.min(W / 1280, b.playH / 720));
    // Scroll-factor-zero objects still receive the camera's zoom about its
    // center. Offset their origin so the background fills the whole viewport.
    const visibleW = W / cam.zoom;
    const visibleH = b.playH / cam.zoom;
    const bgX = (W - visibleW) / 2;

    if (this.isMult || this.isDivision) {
      const image = this.textures.get(this.skyFar.texture.key).getSourceImage();
      const cover = Math.max(visibleW / image.width, visibleH / image.height);
      this.skyFar.setScale(cover).setPosition(bgX + visibleW / 2, (b.playH - visibleH) / 2 + visibleH / 2);
    } else {
      this.skyFar.setPosition(bgX, (b.playH - visibleH) / 2).setSize(visibleW, visibleH);
    }
    // El fondo de Resta ya incluye sus cuerpos celestes.
    this.planetMid.setPosition(bgX, (b.playH + visibleH) / 2).setSize(visibleW, visibleH * 0.64);
    // Resultados grandes y claros en celular/tableta sin cambiar la geometría
    // de las tres rutas. El borde oscuro conserva la lectura sobre cada planeta.
    this.zones.forEach(zone => zone.platforms.forEach(({ label }) => {
      const baseSize = label.getData('baseFontSize') || 44;
      const targetOnScreen = label.getData('advancedMission') ? 26 : 28;
      const maxWidth = label.getData('maxAnswerWidth') || 270;
      label.setFontSize(Math.max(baseSize, Math.ceil(targetOnScreen / cam.zoom)));
      // Las rutas están separadas 360 unidades: el resultado no invade otra respuesta.
      label.setScale(Math.min(1, maxWidth / Math.max(1, label.width)));
    }));
    cam.setDeadzone(300, 200);
    cam.setFollowOffset(0, 60);
    if (this.cameraLocked && this.activeZone) {
      cam.stopFollow();
      cam.centerOn(this.activeZone.centerX, 360);
    } else {
      cam.startFollow(this.player, true, 0.12, 0.12);
    }
  }

  buildInput() {
    this.cursors = this.input.keyboard.createCursorKeys();
    this.keys = this.input.keyboard.addKeys({
      A: Phaser.Input.Keyboard.KeyCodes.A,
      D: Phaser.Input.Keyboard.KeyCodes.D,
      W: Phaser.Input.Keyboard.KeyCodes.W,
    });
    this.input.keyboard.addCapture(['SPACE', 'UP', 'DOWN', 'LEFT', 'RIGHT']);

    this.touch = { left: false, right: false, jump: false, jumpPressed: false };
    this.events.once('shutdown', () => {
      this.input.keyboard.removeCapture(['SPACE', 'UP', 'DOWN', 'LEFT', 'RIGHT']);
    });
  }

  createTouchControls() {
    // Controles táctiles SOLO en dispositivos táctiles; en computadora no aparecen.
    const isTouch =
      (this.sys.game.device.input && this.sys.game.device.input.touch) ||
      (typeof window !== 'undefined' &&
        (window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0));
    if (!isTouch) {
      this.controlButtons = null;
      return;
    }

    const y = GAME.HEIGHT - 76;
    const mk = (x, r, glyph, color) => {
      const c = this.add.circle(x, y, r, color, 0.22).setScrollFactor(0).setDepth(30);
      c.setStrokeStyle(3, color, 0.7);
      const t = this.add
        .text(x, y, glyph, { fontFamily: FONTS.display, fontSize: `${r}px`, fontStyle: '900', color: CSS.white })
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(31);
      c.setInteractive({ useHandCursor: true });
      return { c, t };
    };

    const left = mk(78, 44, '◀', HEX.cyan);
    const right = mk(196, 44, '▶', HEX.cyan);
    const jump = mk(GAME.WIDTH - 96, 56, '▲', HEX.magenta);
    this.controlButtons = { left, right, jump };

    const press = (obj, key) => {
      obj.c.on('pointerdown', () => {
        this.touch[key] = true;
        if (key === 'jump') this.touch.jumpPressed = true;
        obj.c.setFillStyle(obj.c.fillColor, 0.5);
      });
      const release = () => {
        this.touch[key] = false;
        obj.c.setFillStyle(obj.c.fillColor, 0.22);
      };
      obj.c.on('pointerup', release);
      obj.c.on('pointerout', release);
    };
    press(left, 'left');
    press(right, 'right');
    press(jump, 'jump');
  }

  // API sencilla para pruebas automatizadas / integración futura de controles.
  setControl(name, value) {
    if (!this.touch || this.missionPaused) return;
    if (name === 'jump') {
      this.touch.jump = value;
      if (value) this.touch.jumpPressed = true;
    } else {
      this.touch[name] = value;
    }
  }

  startTutorial() {
    if (!this.tutorialActive || this.levelEnded) return;
    // El mismo orden se valida con teclado o con botones táctiles. En móvil
    // nunca avanza por tiempo: cada instrucción espera la acción real.
    this.tutorialStep = 'right';
    this.tutorialStartX = this.player.x;
    this.events.emit('tutorial-step', { step: 'right' });
  }

  advanceTutorial(nextStep) {
    if (!this.tutorialActive) return;
    this.tutorialStep = 'transition';
    this.events.emit('tutorial-step', { step: 'success' });
    this.time.delayedCall(320, () => {
      if (!this.tutorialActive) return;
      if (!nextStep) {
        this.completeTutorial();
        return;
      }
      this.tutorialStep = nextStep;
      if (nextStep === 'left') this.tutorialRightX = this.player.x;
      this.events.emit('tutorial-step', { step: nextStep });
    });
  }

  updateTutorialProgress() {
    if (!this.tutorialActive) return;
    if (this.tutorialStep === 'right' && this.player.x >= this.tutorialStartX + 54) {
      this.advanceTutorial('left');
    } else if (this.tutorialStep === 'left' && this.player.x <= this.tutorialRightX - 54) {
      this.advanceTutorial('jump');
    }
  }

  completeTutorial() {
    if (!this.tutorialActive) return;
    this.tutorialActive = false;
    this.tutorialStep = null;
    this.gs.setClockActive(true);
    this.tutorialAnswerHintPending = true;
    PlayerData.markTutorialSeen();
    this.events.emit('tutorial-complete');
  }

  activateZone(zone) {
    this.activeIndex = zone.index;
    this.activeZone = zone;
    this.answered = false;
    this.checkpoint = { x: zone.spawnX, y: LEVEL.PLAT_TOP - 60 };
    this.gs.setProblem(zone.problem);

    // Después del entrenamiento, el primer reto recuerda el objetivo real.
    if (zone.index === 0 && this.tutorialAnswerHintPending) {
      this.tutorialAnswerHintPending = false;
      this.events.emit('tutorial-first-zone');
    }

    // Bloquear el desplazamiento de la cámara y centrar la zona (todas las respuestas visibles).
    this.cameraLocked = true;
    const cam = this.cameras.main;
    cam.stopFollow();
    cam.pan(zone.centerX, 360, 450, 'Sine.easeInOut');
  }

  onAnswerLand(player, plat) {
    if (this.answered || this.levelEnded) return;
    if (plat.getData('challenge') !== this.activeIndex) return;
    if (!(player.body.blocked.down || player.body.touching.down)) return;

    this.answered = true;
    const zone = this.zones[this.activeIndex];
    if (plat.getData('correct')) this.handleCorrect(plat, zone);
    else this.handleWrong(plat, zone);
  }

  // Confirmación visual uniforme: las texturas de cada planeta son distintas,
  // por eso la capa verde se dibuja sobre toda la plataforma en vez de teñirla.
  markCorrectPlatform(plat) {
    const width = plat.displayWidth;
    const height = plat.displayHeight;
    const left = plat.x - width / 2;
    const top = plat.y - height / 2;
    const overlay = this.add.graphics().setDepth(5.5);

    // El verde cubre la tarjeta completa, conserva un poco de la textura del
    // planeta y añade un borde nítido que se lee igual en los cuatro mundos.
    overlay.fillStyle(0x10c978, 0.78);
    overlay.fillRoundedRect(left, top, width, height, 12);
    overlay.lineStyle(4, 0xc3ffe0, 1);
    overlay.strokeRoundedRect(left + 2, top + 2, width - 4, height - 4, 10);

    // Conserva las cuatro luces del panel original. El menta casi blanco
    // mantiene la lectura de acierto sin convertir la pieza en un bloque plano.
    const lightY = top + height * 0.63;
    const lightSpacing = width * 0.18;
    overlay.fillStyle(0xe8fff5, 0.9);
    [-1.5, -0.5, 0.5, 1.5].forEach((slot) => {
      const lightX = plat.x + slot * lightSpacing;
      overlay.fillRoundedRect(lightX - 3.5, lightY - 3.5, 7, 7, 2);
    });

    overlay.setAlpha(0);
    this.tweens.add({
      targets: overlay,
      alpha: 1,
      duration: 130,
      ease: 'Sine.out',
    });
    return overlay;
  }

  handleCorrect(plat, zone) {
    zone.solved = true;
    this.markCorrectPlatform(plat);
    PlayerData.recordSkillAttempt(this.planetId, this.mission.index, zone.problem.skillTag, true);
    const pts = this.gs.correct();
    // La guía termina solamente cuando el primer ejercicio se resolvió bien.
    if (this.tutorialActive && this.tutorialStep === 'answer' && zone.index === 0) {
      this.completeTutorial();
    }
    const route = zone.platforms[plat.getData('index')];

    // La confirmación sucede al instante: verde, puntos, destello y un sonido
    // inequívoco. El juego nunca pausa, cambia de cámara ni bloquea los controles.
    this.burst(plat.x, plat.y - 40, HEX.green);
    this.floatingText(plat.x, plat.y - 82, `+${pts} pts`, CSS.gold);
    this.banner('¡CORRECTO!', CSS.green);
    this.cameras.main.flash(120, 40, 255, 150, false);

    if (route) {
      this.tweens.add({
        targets: route.crystal,
        scaleX: route.crystal.scaleX * 1.12,
        scaleY: route.crystal.scaleY * 1.12,
        duration: 130,
        yoyo: true,
        ease: 'Sine.out',
      });
    }

    // La cámara retoma inmediatamente la aventura, sin cambiar la perspectiva.
    this.cameraLocked = false;
    this.activeZone = null;
    const cam = this.cameras.main;
    cam.startFollow(this.player, true, 0.12, 0.12);
    cam.setFollowOffset(0, 60);
  }

  handleWrong(plat, zone) {
    plat.setTint(HEX.red);
    this.burst(plat.x, plat.y - 20, HEX.red);
    this.banner('¡UPS! INTÉNTALO DE NUEVO', CSS.red);
    this.cameras.main.shake(200, 0.01);

    // La ruta incorrecta se desvanece; el jugador cae a la plataforma segura (sin peligro).
    plat.body.enable = false;
    const pd = zone.platforms[plat.getData('index')];
    this.tweens.add({ targets: plat, y: plat.y + 220, alpha: 0.15, angle: 10, duration: 600, ease: 'Quad.in' });
    if (pd) this.tweens.add({ targets: [pd.crystal, pd.label], y: '+=220', alpha: 0.15, duration: 600 });

    PlayerData.recordSkillAttempt(this.planetId, this.mission.index, zone.problem.skillTag, false);
    this.gs.wrong();

    // Restaurar la ruta para poder reintentar el MISMO reto.
    this.time.delayedCall(800, () => {
      if (this.levelEnded || zone.solved) return;
      this.restoreZone(zone);
      this.answered = false;
    });
  }

  restoreZone(zone) {
    zone.platforms.forEach((pd) => {
      pd.plat.body.enable = true;
      pd.plat.clearTint();
      // La plataforma de Resta recupera naturalmente su textura clara.
      pd.plat.setAlpha(1).setAngle(0).setPosition(pd.home.x, pd.home.y);
      pd.plat.body.checkCollision.down = false;
      pd.plat.body.checkCollision.left = false;
      pd.plat.body.checkCollision.right = false;
      pd.crystal.setAlpha(1).setY(pd.crystalHome);
      pd.label.setAlpha(1).setY(pd.labelHome);
    });
  }

  respawn() {
    // El personaje siempre vuelve a una posición física segura sobre la plataforma.
    // reset() limpia velocidad, contacto y la coordenada interna de Arcade Physics;
    // setPosition() por sí solo puede dejar el cuerpo debajo del mundo tras una caída.
    if (this.levelEnded || this.respawnQueued || !this.player?.body) return;
    this.respawnQueued = true;

    const safeX = this.checkpoint.x;
    const safeY = Math.min(this.checkpoint.y, LEVEL.PLAT_TOP - 88);
    this.player.body.enable = true;
    this.player.setPosition(safeX, safeY);
    this.player.body.reset(safeX, safeY);
    this.player.setVelocity(0, 0);
    this.player.setAcceleration(0, 0);
    this.player.setAngularVelocity(0);
    this.player.setRotation(0);
    this.player.coyote = PHYS.COYOTE_MS;
    this.player.buffer = 0;
    this.player.cutApplied = false;

    // Restablece el seguimiento normal si la caída ocurrió entre retos.
    if (!this.cameraLocked) {
      this.cameras.main.startFollow(this.player, true, 0.12, 0.12);
      this.cameras.main.setFollowOffset(0, 60);
    }

    this.cameras.main.flash(120, 60, 40, 120);
    this.time.delayedCall(180, () => {
      this.respawnQueued = false;
    });
  }

  collectBonus(c) {
    if (!c.active) return;
    c.destroy();
    const pts = this.gs.addBonus();
    this.burst(c.x, c.y, HEX.green);
    this.floatingText(c.x, c.y - 20, `+${pts}`, CSS.gold);
    this.audio && this.audio.playCollect();
  }

  reachGoal() {
    if (this.levelEnded) return;
    this.gs.win();
  }

  burst(x, y, tint) {
    const e = this.add.particles(x, y, 'spark', {
      speed: { min: 120, max: 360 },
      angle: { min: 0, max: 360 },
      scale: { start: 1, end: 0 },
      alpha: { start: 1, end: 0 },
      lifespan: 620,
      quantity: 26,
      tint: [tint, HEX.white],
      blendMode: 'ADD',
    });
    e.setDepth(20);
    e.explode(26, x, y);
    this.time.delayedCall(700, () => e.destroy());
  }

  floatingText(x, y, text, color) {
    const t = this.add
      .text(x, y, text, { fontFamily: FONTS.display, fontSize: '34px', fontStyle: '900', color })
      .setOrigin(0.5)
      .setDepth(21);
    this.tweens.add({ targets: t, y: y - 70, alpha: 0, duration: 900, ease: 'Quad.out', onComplete: () => t.destroy() });
  }

  banner(text, color) {
    const view = this.cameras.main.worldView;
    const t = this.add
      .text(view.centerX, view.y + view.height * 0.22, text, {
        fontFamily: FONTS.display,
        fontSize: '44px',
        fontStyle: '900',
        color,
      })
      .setOrigin(0.5)
      .setDepth(22)
      .setShadow(0, 0, '#000', 12, true, true);
    t.setScale(0.6);
    this.tweens.add({ targets: t, scale: 1, duration: 220, ease: 'Back.out' });
    this.tweens.add({ targets: t, alpha: 0, delay: 800, duration: 400, onComplete: () => t.destroy() });
  }

  runDevFinalPreview() {
    const scenario = this.devPreview?.scenario;
    if (!scenario || this.levelEnded) return;

    // Datos exclusivamente visuales para QA: no se resuelven ejercicios, no
    // se dispara GameState y no se registra ningún intento del jugador.
    this.gs.setClockActive(false);
    this.gs.solved = scenario.correct;
    this.gs.wrongCount = scenario.wrong;
    this.gs.lives = scenario.lives;
    this.gs.totalChallenges = 10;
    this.gs.timeLeft = scenario.timeLeft;
    this.gs.elapsedTime = scenario.elapsedTime == null ? this.mission.time : scenario.elapsedTime;
    this.gs.bestStreak = scenario.correct;
    this.gs.scoreSys.total = scenario.score;
    this.gs.scoreSys.events = [];
    this.gs.over = true;
    this.gs.won = scenario.won;
    this.gs.setProgress(1);

    // La cámara llega al mismo punto de META desde el que se reproduce la
    // secuencia oficial; la nave y el astronauta no son una animación DEV.
    const previewX = this.goalX - 44;
    const previewY = LEVEL.PLAT_TOP - 60;
    this.player.setPosition(previewX, previewY);
    this.player.body.reset(previewX, previewY);
    this.cameras.main.stopFollow();
    this.cameras.main.centerOn(this.goalX, 360);
    this.endLevel(scenario.won);
  }

  endLevel(won) {
    if (this.levelEnded) return;
    this.levelEnded = true;
    this.player.setVelocity(0, 0);
    this.player.body.enable = false;

    if (won) {
      this.playMissionDeparture();
      this.time.delayedCall(3200, () => this.openCosmicValueCapsule());
    } else {
      this.time.delayedCall(700, () => this.showMissionResults());
    }
  }

  // La rueda/portal sigue siendo la META: al activarse funciona como baliza
  // de aterrizaje. Esta secuencia es compartida por suma, resta, multiplicación
  // y división; solo toma los colores propios del planeta.
  playMissionDeparture() {
    const palette = this.isResta
      ? { glow: 0x8ff5e5, accent: 0xffe89a }
      : this.isMult
        ? { glow: HEX.green, accent: HEX.gold }
        : this.isDivision
          ? { glow: 0x75f0ff, accent: 0xb7a3ff }
          : { glow: HEX.cyan, accent: HEX.magenta };

    const landX = this.goalX + 40;
    // Anclaje real al suelo: las patas de la nave, no su centro, llegan al piso.
    const closedGroundY = LEVEL.PLAT_TOP - 74;
    const openGroundY = LEVEL.PLAT_TOP - 79;
    const startY = closedGroundY - 250;

    // La rueda/portal se convierte en una baliza de aterrizaje.
    this.goal.setAlpha(0.34);
    const beacon = this.add.circle(this.goalX, LEVEL.PLAT_TOP - 16, 86, palette.glow, 0.12)
      .setStrokeStyle(4, palette.glow, 0.9)
      .setDepth(28);
    this.tweens.add({
      targets: beacon,
      scale: 1.3,
      alpha: 0,
      duration: 520,
      yoyo: true,
      repeat: 2,
      ease: 'Sine.inOut',
    });

    // Dos estados del mismo sprite: aterriza cerrado, abre la rampa solo después
    // de tocar el suelo y vuelve a cerrarse antes de salir.
    const shipClosed = this.add.image(landX, startY, 'mission_shuttle_closed')
      .setOrigin(0.5)
      .setDepth(35)
      .setScale(0.46);
    const shipOpen = this.add.image(landX, openGroundY, 'mission_shuttle_open')
      .setOrigin(0.5)
      .setDepth(36)
      .setScale(0.46)
      .setAlpha(0);

    const message = this.add.text(this.goalX, LEVEL.PLAT_TOP - 285, '¡MISIÓN COMPLETADA!', {
      fontFamily: FONTS.display,
      fontSize: '32px',
      fontStyle: '900',
      color: CSS.gold,
      backgroundColor: '#061329',
      padding: { x: 18, y: 9 },
    })
      .setOrigin(0.5)
      .setDepth(38)
      .setStroke('#020814', 4)
      .setShadow(0, 3, '#000000', 5, true, true)
      .setAlpha(0);

    this.audio && this.audio.playDeparture();
    this.burst(this.goalX, LEVEL.PLAT_TOP - 90, palette.glow);
    this.tweens.add({ targets: message, alpha: 1, duration: 180, ease: 'Sine.out' });

    // 1) Descenso cerrado y aterrizaje claramente legible.
    this.tweens.add({
      targets: shipClosed,
      y: closedGroundY,
      duration: 650,
      ease: 'Quad.out',
      onComplete: () => {
        this.audio && this.audio.playLand();
        this.tweens.add({
          targets: shipClosed,
          y: closedGroundY - 8,
          duration: 110,
          yoyo: true,
          repeat: 1,
          ease: 'Sine.inOut',
        });
      },
    });

    // 2) Después de aterrizar, abre la puerta y aparece la rampa.
    this.time.delayedCall(900, () => {
      this.tweens.add({ targets: shipClosed, alpha: 0, duration: 240, ease: 'Sine.inOut' });
      this.tweens.add({ targets: shipOpen, alpha: 1, duration: 240, ease: 'Sine.inOut' });
    });

    // 3) El personaje entra a la nave; su física ya está bloqueada porque la
    // misión terminó, así que esto no puede alterar salto, rutas ni controles.
    this.time.delayedCall(1200, () => {
      this.tweens.add({
        targets: this.player,
        x: landX - 18,
        duration: 560,
        ease: 'Sine.inOut',
        onComplete: () => {
          this.player.setVisible(false);
          this.burst(landX - 18, LEVEL.PLAT_TOP - 72, palette.accent);
        },
      });
    });

    // 4) Cierra la puerta antes del despegue.
    this.time.delayedCall(1840, () => {
      shipClosed.setPosition(landX, closedGroundY).setAlpha(0);
      this.tweens.add({ targets: shipOpen, alpha: 0, duration: 220, ease: 'Sine.inOut' });
      this.tweens.add({ targets: shipClosed, alpha: 1, duration: 220, ease: 'Sine.inOut' });
    });

    // 5) Sale hacia la siguiente misión.
    this.time.delayedCall(2110, () => {
      this.tweens.add({
        targets: shipClosed,
        x: landX + 520,
        y: closedGroundY - 410,
        scaleX: 0.56,
        scaleY: 0.56,
        alpha: 0,
        duration: 820,
        ease: 'Quad.in',
      });
      this.tweens.add({ targets: [message, beacon], alpha: 0, y: '-=22', duration: 420, ease: 'Sine.in' });
    });
  }

  openCosmicValueCapsule() {
    // La cápsula solo se abre tras una victoria oficial. El resultado de la
    // carrera ya está calculado y se conserva intacto hasta pulsar Continuar.
    if (this.practiceMode || typeof window === 'undefined') {
      this.showMissionResults();
      return;
    }

    this.cosmicCapsuleSessionId = 'mission-' + Date.now() + '-' + Phaser.Math.Between(1000, 9999);
    window.dispatchEvent(
      new CustomEvent('tmag:cosmic-value-capsule-open', {
        detail: {
          sessionId: this.cosmicCapsuleSessionId,
          planetId: this.planetId,
          message: this.devPreview?.capsuleMessage || getNextCosmicValueMessage(),
        },
      }),
    );
  }

  showMissionResults() {
    this.scene.stop('HUDScene');
    this.scene.start('ResultScene', {
      ...this.gs.toRun(),
      practiceMode: this.practiceMode,
      practiceFocus: this.practiceFocus,
      devPreview: !!this.devPreview,
      ...(this.progressAttemptId ? { progressAttemptId: this.progressAttemptId } : {}),
    });
  }

  pauseMission() {
    if (this.levelEnded || this.missionPaused) return false;
    this.missionPaused = true;
    Object.assign(this.touch || {}, { left: false, right: false, jump: false, jumpPressed: false });
    this.physics.world.pause();
    this.tweens.pauseAll?.();
    this.scene.pause();
    return true;
  }

  resumeMission() {
    if (!this.missionPaused) return false;
    this.missionPaused = false;
    this.physics.world.resume();
    this.tweens.resumeAll?.();
    this.scene.resume();
    return true;
  }

  restartMission() {
    if (this.levelEnded) return;
    this.missionPaused = false;
    this.physics.world.resume();
    this.tweens.resumeAll?.();
    this.scene.resume();
    this.scene.stop('HUDScene');
    if (this.playAuthorizationActive) this.registry.set('adaptivePractice', this.practiceMode);
    this.scene.restart();
  }

  exitMission() {
    if (this.levelEnded) return;
    this.missionPaused = false;
    this.physics.world.resume();
    this.tweens.resumeAll?.();
    this.scene.resume();
    this.scene.stop('HUDScene');
    this.scene.start('MissionScene');
  }

  update(time, delta) {
    if (this.awaitingPlayAuthorization) return;
    if (this.levelEnded || this.missionPaused) return;
    const dt = delta;

    const rawInput = {
      left: this.cursors.left.isDown || this.keys.A.isDown || this.touch.left,
      right: this.cursors.right.isDown || this.keys.D.isDown || this.touch.right,
      jumpDown:
        Phaser.Input.Keyboard.JustDown(this.cursors.space) ||
        Phaser.Input.Keyboard.JustDown(this.cursors.up) ||
        Phaser.Input.Keyboard.JustDown(this.keys.W) ||
        this.touch.jumpPressed,
      jumpHeld: this.cursors.space.isDown || this.cursors.up.isDown || this.keys.W.isDown || this.touch.jump,
    };
    this.touch.jumpPressed = false;
    // Durante los tres primeros pasos solo responde el botón solicitado.
    // Así la instrucción no desaparece ni se puede saltar el orden.
    const guided = this.tutorialActive && this.tutorialStep !== 'answer';
    const input = guided
      ? {
        left: this.tutorialStep === 'left' && rawInput.left,
        right: this.tutorialStep === 'right' && rawInput.right,
        jumpDown: this.tutorialStep === 'jump' && rawInput.jumpDown,
        jumpHeld: this.tutorialStep === 'jump' && rawInput.jumpHeld,
      }
      : rawInput;
    this.player.handleInput(input, dt);

    // Límite izquierdo del mundo.
    if (this.player.x < 24) {
      this.player.x = 24;
      if (this.player.body.velocity.x < 0) this.player.setVelocityX(0);
    }

    // Durante la guía se usa únicamente la plataforma inicial: el estudiante
    // puede practicar movimientos sin entrar todavía a las respuestas.
    if (this.tutorialActive && this.tutorialStep !== 'answer') {
      this.player.x = Phaser.Math.Clamp(this.player.x, 70, 330);
      this.updateTutorialProgress();
    } else if (this.cameraLocked && this.activeZone) {
      // Durante la zona de decisión, mantener al jugador dentro de la plataforma segura.
      this.player.x = Phaser.Math.Clamp(this.player.x, this.activeZone.minX, this.activeZone.maxX);
    }

    // Caída al vacío (solo posible en tramos de plataformeo) -> reaparecer en el checkpoint.
    if (this.player.y > GAME.HEIGHT + 140) this.respawn();

    // Activar la zona de decisión al aproximarse.
    if ((!this.tutorialActive || this.tutorialStep === 'answer') && !this.cameraLocked) {
      for (const zone of this.zones) {
        if (zone.index > this.activeIndex && this.player.x > zone.activationX) {
          this.activateZone(zone);
          break;
        }
      }
    }

    // Tiempo y progreso.
    this.gs.tick(dt / 1000);
    this.gs.setProgress(this.player.x / this.goalX);

    // Parallax.
    const sx = this.cameras.main.scrollX;
    // El paisaje diurno permanece estable para evitar repeticiones visibles; Suma conserva su parallax.
    if (!this.isResta && !this.isMult && !this.isDivision) this.skyFar.tilePositionX = sx * 0.1;
    if (!this.isResta && !this.isMult && !this.isDivision) this.planetMid.tilePositionX = sx * 0.4;
  }
}
